/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ------------------------------------------------------------------
 * LOT-CUSTOM 2026-08-31 — product-card swatch sync + instant add-to-cart.
 * MOVED VERBATIM from snippets/product-item.liquid (md5 f0855b231e243ff91aa791865baa3327).
 * WHY: that <script> was rendered once PER CARD. Pages rendering N cards
 * registered N duplicate document-level listeners (16 on a normal PDP —
 * idempotent, so invisible, but 16x work per click). Pages that render ZERO
 * cards server-side got NO handler at all: the crest PDP's cards arrive via
 * <product-recommendations> fetch + innerHTML, and injected <script> never
 * executes, so its swatches had never worked. custom.js is loaded once on
 * every page (theme.liquid, defer), so one binding covers AJAX-injected
 * cards too. Do NOT move this back into a per-card snippet.
 * ------------------------------------------------------------------ */
(function () {
  document.addEventListener('change', function (event) {
    var swatch = event.target.closest && event.target.closest('.color-swatch__radio');
    if (!swatch) return;

    var productItem = swatch.closest('.product-item');
    if (!productItem) return;

    var btn = productItem.querySelector('.product-item__buy-now');
    var available = swatch.getAttribute('data-variant-available') === 'true';
    var variantId = swatch.getAttribute('data-variant-id');

    if (btn) {
      btn.disabled = !available;
      btn.textContent = available ? (btn.getAttribute('data-add-label') || 'Add to cart') : 'Sold out';
    }

    var imageWrapper = productItem.querySelector('.product-item__image-wrapper');
    if (imageWrapper) {
      imageWrapper.style.filter = available ? '' : 'grayscale(1)';
      imageWrapper.style.opacity = available ? '' : '0.55';
    }

    var infoWrapper = productItem.querySelector('.product-item__info');
    if (infoWrapper) {
      infoWrapper.style.opacity = available ? '' : '0.55';
    }

    var hiddenInput = productItem.querySelector('input[name="id"]');
    if (hiddenInput && variantId) {
      hiddenInput.value = variantId;
    }

    var mediaId = swatch.getAttribute('data-media-id');
    if (!mediaId) return;

    productItem.querySelectorAll('.product-item__primary-image').forEach(function (img) {
      img.hidden = img.getAttribute('data-media-id') !== mediaId;
    });
  });

  document.addEventListener('click', async function (event) {
    var btn = event.target.closest && event.target.closest('.product-item__buy-now');
    if (!btn || btn.disabled) return;

    event.preventDefault();

    var productItem = btn.closest('.product-item');
    if (!productItem) return;

    var hiddenInput = productItem.querySelector('input[name="id"]');
    var variantId = hiddenInput ? hiddenInput.value : null;
    if (!variantId) {
      console.error('No variant ID found for quick add');
      return;
    }

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    var miniCartDrawer = document.getElementById('mini-cart');
    if (miniCartDrawer) {
      miniCartDrawer.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      miniCartDrawer.removeAttribute('hidden');
      miniCartDrawer.setAttribute('aria-hidden', 'false');

      var cartToggle = document.querySelector(
        '[aria-controls="mini-cart"]:not([aria-expanded="true"]), [data-cart-toggle], .header__icon--cart'
      );
      if (cartToggle) cartToggle.click();
    }

    try {
      var addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });

      if (!addResponse.ok) throw new Error('Add to cart failed');
      await addResponse.json();

      var drawer = document.getElementById('mini-cart');
      if (drawer) {
        var sectionResponse = await fetch(window.location.pathname + '?section_id=mini-cart');
        var sectionHtml = await sectionResponse.text();
        var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var newDrawer = doc.getElementById('mini-cart');
        if (newDrawer) drawer.innerHTML = newDrawer.innerHTML;
      }

      var cartCountEls = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count');
      if (cartCountEls.length) {
        var cartResponse = await fetch('/cart.js');
        var cartData = await cartResponse.json();
        cartCountEls.forEach(function (el) {
          el.textContent = cartData.item_count;
          el.hidden = cartData.item_count === 0;
        });
      }

      btn.textContent = 'Added!';
      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);
    } catch (error) {
      console.error(error);
      btn.textContent = 'Error, try again';
      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);
    }
  });
})();

/* LOT-CUSTOM 2026-09-03 (Claude) — SMART SEARCH AUTOCOMPLETE, behaviour.
MOVED here from sections/header.liquid on 2026-09-03 (~16 KB that used to ship inline
on every page). Original author Abhinav 2026-08-31, with the 08-31 re-entrancy fix and the
09-01 multi-word fix preserved verbatim.

🔴 THE ONE PART THAT COULD NOT MOVE: the product-title list is generated by LIQUID from the live
catalogue, and a .js asset cannot render Liquid. header.liquid still emits it inline as
`window.lotProductTitles`, and this file reads it. custom.js is loaded with `defer` in
layout/theme.liquid:110, so the inline array is always set before this runs. If the search stops
completing, check that inline block first. */
(() => {

  /* ============================================================
     LOT SMART SEARCH
     ============================================================ */

  let lotSuggestion = '';
  let lotInput = null;

  let lotProductTitles = (window.lotProductTitles || []);

  let productsLoaded = false;
  let productsLoading = false;


  /* ============================================================
     CREATE AUTOCOMPLETE UI
     ============================================================ */

  const suggestionElement =
    document.createElement('div');

  suggestionElement.id =
    'lot-smart-search-suggestion';

  suggestionElement.innerHTML = `
    <span class="lot-typed"></span><span class="lot-rest"></span>
  `;


  const arrowElement =
    document.createElement('button');

  arrowElement.id =
    'lot-smart-search-arrow';

  arrowElement.type =
    'button';

  arrowElement.setAttribute(
    'aria-label',
    'Complete search suggestion'
  );

  arrowElement.setAttribute(
    'title',
    'Complete suggestion'
  );

  arrowElement.innerHTML = '→';


  document.body.appendChild(
    suggestionElement
  );

  document.body.appendChild(
    arrowElement
  );


  const typedElement =
    suggestionElement.querySelector(
      '.lot-typed'
    );


  const restElement =
    suggestionElement.querySelector(
      '.lot-rest'
    );


  /* ============================================================
     PRODUCT TITLES FROM SHOPIFY
     
     IMPORTANT:
     We use Liquid here instead of predictive-search.
     
     Therefore we're NOT limited to Shopify's 10
     predictive-search results.
     ============================================================ */



  productsLoaded = true;


  /* ============================================================
     CLEAN PRODUCT TITLE
     ============================================================ */

  function cleanTitle(title) {

    return String(title || '')
      .replace(/\s+/g, ' ')
      .trim();

  }


  /* ============================================================
     EXTRACT WORDS
     
     Example:
     
     "L.O.T Cars Shadow – RC Drift Car"
     
     becomes:
     
     L.O.T
     Cars
     Shadow
     RC
     Drift
     Car
     ============================================================ */

  function getWords(title) {

    const cleaned =
      cleanTitle(title);


    return cleaned.match(
      /[A-Za-z0-9][A-Za-z0-9.'-]*/g
    ) || [];

  }


  /* ============================================================
     FIND BEST AUTOCOMPLETE
     ============================================================ */

  function findBestSuggestion(query) {

    const typed =
      query.toLowerCase();


    if (!typed) {
      return null;
    }


    /*
     * LOT-CUSTOM 2026-09-01 (s45, Claude): MULTI-WORD SUPPORT.
     *
     * getWords() splits titles on non-alphanumerics, so no candidate word ever contains a
     * space. Matching the WHOLE input against those words therefore returned null for ANY
     * query containing a space -- "rc dri" suggested nothing at all. We now complete the
     * LAST token and re-prepend the earlier ones untouched.
     *
     * 🔴 `word` below is therefore the FULL completed input, not just the matched token.
     * That is load-bearing downstream: showSuggestion() slices the grey remainder as
     * word.substring(typed.length), and completeSuggestion() assigns the whole thing to
     * input.value. Returning only the token would corrupt both.
     *
     * `lead` keeps the customer's ORIGINAL casing; only the completed token takes the
     * catalogue's casing -- exactly as the single-word path always did ("gho" -> "Ghost").
     */
    const splitAt =
      typed.lastIndexOf(' ');

    const lead =
      query.slice(0, splitAt + 1);

    const lastToken =
      typed.slice(splitAt + 1);


    /*
     * Trailing space -- nothing to complete yet.
     */
    if (!lastToken) {
      return null;
    }


    /*
     * Every EARLIER token must appear in the title, so "rc dri" completes from an RC
     * product rather than from any product that merely contains a "dri" word.
     */
    const leadTokens =
      lead.toLowerCase().match(
        /[a-z0-9][a-z0-9.'-]*/g
      ) || [];


    const candidates = [];


    /* ========================================================
       SEARCH EVERY PRODUCT TITLE
       ======================================================== */

    for (
      const title of lotProductTitles
    ) {

      const lowerTitle =
        title.toLowerCase();


      if (
        leadTokens.some(
          token => !lowerTitle.includes(token)
        )
      ) {
        continue;
      }


      const words =
        getWords(title);


      for (
        const word of words
      ) {

        const lowerWord =
          word.toLowerCase();


        /*
         * Exact prefix match.
         *
         * sha → Shadow
         * gho → Ghost
         * fla → Flare
         */
        if (
          lowerWord.startsWith(lastToken)
        ) {

          /*
           * Don't suggest the exact word
           * the customer already typed.
           */
          if (
            lowerWord === lastToken
          ) {
            continue;
          }


          candidates.push({
            word: lead + word,
            title: title
          });

        }

      }

    }


    if (
      candidates.length === 0
    ) {

      return null;

    }


    /* ========================================================
       REMOVE DUPLICATE WORDS
       ======================================================== */

    const unique = [];

    const seen =
      new Set();


    for (
      const candidate of candidates
    ) {

      const key =
        candidate.word.toLowerCase();


      if (
        seen.has(key)
      ) {
        continue;
      }


      seen.add(key);

      unique.push(
        candidate
      );

    }


    /* ========================================================
       SORT
       
       Priority:
       
       1. Word exactly starts with typed text
       2. Shorter word first
       3. Alphabetical
       ======================================================== */

    unique.sort(
      (a, b) => {

        const aWord =
          a.word.toLowerCase();

        const bWord =
          b.word.toLowerCase();


        /*
         * Shorter completion first.
         */
        if (
          aWord.length !==
          bWord.length
        ) {

          return (
            aWord.length -
            bWord.length
          );

        }


        return aWord.localeCompare(
          bWord
        );

      }
    );


    return unique[0];

  }


  /* ============================================================
     IS SEARCH INPUT?
     ============================================================ */

  function isSearchInput(element) {

    if (!element) {
      return false;
    }


    if (
      element.tagName !== 'INPUT'
    ) {

      return false;

    }


    if (
      element.type !== 'text' &&
      element.type !== 'search'
    ) {

      return false;

    }


    /*
     * Shopify search input.
     */
    if (
      element.name === 'q'
    ) {

      return true;

    }


    /*
     * Shopify predictive search.
     */
    if (
      element.classList &&
      element.classList.contains(
        'predictive-search__input'
      )
    ) {

      return true;

    }


    return false;

  }


  /* ============================================================
     POSITION AUTOCOMPLETE
     ============================================================ */

  function positionAutocomplete(
    input
  ) {

    if (!input) {
      return;
    }


    const rect =
      input.getBoundingClientRect();


    let left =
      rect.left;


    let top =
      rect.top +
      (rect.height / 2);


    try {

      const style =
        window.getComputedStyle(
          input
        );


      /*
       * Use input padding so the suggestion
       * starts exactly where typed text starts.
       */
      left +=
        parseFloat(
          style.paddingLeft
        ) || 0;


      suggestionElement.style.fontFamily =
        style.fontFamily;


      suggestionElement.style.fontSize =
        style.fontSize;


      suggestionElement.style.fontWeight =
        style.fontWeight;


      suggestionElement.style.letterSpacing =
        style.letterSpacing;


      suggestionElement.style.lineHeight =
        style.lineHeight;

    }
    catch (error) {}


    suggestionElement.style.left =
      left + 'px';


    suggestionElement.style.top =
      top + 'px';


    /* ========================================================
       ARROW POSITION
       ======================================================== */

    /*
     * Mobile search drawers are often narrower.
     *
     * Put our arrow just before Shopify's
     * close button / right side.
     */

    const arrowSize =
      window.innerWidth <= 740
        ? 30
        : 34;


    let arrowLeft =
      rect.right -
      arrowSize -
      42;


    /*
     * Don't allow it to overlap the
     * left side of the input.
     */
    if (
      arrowLeft <
      rect.left + 50
    ) {

      arrowLeft =
        rect.left + 50;

    }


    arrowElement.style.left =
      arrowLeft + 'px';


    arrowElement.style.top =
      (
        rect.top +
        (rect.height / 2) -
        (arrowSize / 2)
      ) + 'px';

  }


  /* ============================================================
     HIDE
     ============================================================ */

  function hideSuggestion() {

    lotSuggestion =
      '';

    lotInput =
      null;


    suggestionElement.style.display =
      'none';


    arrowElement.style.display =
      'none';


    typedElement.textContent =
      '';


    restElement.textContent =
      '';

  }


  /* ============================================================
     SHOW
     ============================================================ */

  function showSuggestion(
    input,
    typed,
    suggestion
  ) {

    lotInput =
      input;


    lotSuggestion =
      suggestion;


    /*
     * White typed letters.
     */
    typedElement.textContent =
      typed;


    /*
     * Grey remaining letters.
     */
    restElement.textContent =
      suggestion.substring(
        typed.length
      );


    positionAutocomplete(
      input
    );


    suggestionElement.style.display =
      'block';


    arrowElement.style.display =
      'flex';

  }


  /* ============================================================
     HANDLE INPUT
     ============================================================ */

  function handleInput(
    input
  ) {

    if (
      !isSearchInput(input)
    ) {

      return;

    }


    lotInput =
      input;


    const value =
      input.value.trim();


    /*
     * Nothing typed.
     */
    if (!value) {

      hideSuggestion();

      return;

    }


    /*
     * Start after 1 character.
     *
     * This makes mobile autocomplete
     * feel faster.
     */
    if (
      value.length < 1
    ) {

      hideSuggestion();

      return;

    }


    /*
     * Find suggestion from ALL loaded products.
     */
    const match =
      findBestSuggestion(
        value
      );


    if (!match) {

      hideSuggestion();

      return;

    }


    /*
     * Don't show if the suggestion
     * somehow equals the input.
     */
    if (
      match.word.toLowerCase() ===
      value.toLowerCase()
    ) {

      hideSuggestion();

      return;

    }


    showSuggestion(
      input,
      value,
      match.word
    );

  }


  /* ============================================================
     INPUT EVENT
     
     Capture phase ensures this also catches
     Shopify's dynamically-created drawer input.
     ============================================================ */

  document.addEventListener(
    'input',
    event => {

      const input =
        event.target;


      if (
        !isSearchInput(input)
      ) {

        return;

      }


      handleInput(
        input
      );

    },
    true
  );


  /* ============================================================
     FOCUS
     ============================================================ */

  document.addEventListener(
    'focusin',
    event => {

      const input =
        event.target;


      if (
        !isSearchInput(input)
      ) {

        return;

      }


      lotInput =
        input;


      const value =
        input.value.trim();


      if (value) {

        handleInput(
          input
        );

      }

    },
    true
  );


  /* ============================================================
     RIGHT ARROW KEY
     ============================================================ */

  document.addEventListener(
    'keydown',
    event => {

      if (
        event.key !== 'ArrowRight'
      ) {

        return;

      }


      if (
        !lotInput ||
        !lotSuggestion
      ) {

        return;

      }


      if (
        event.target !== lotInput
      ) {

        return;

      }


      /*
       * Only complete when cursor
       * is at the end.
       */
      try {

        if (
          lotInput.selectionStart !==
          lotInput.value.length
        ) {

          return;

        }

      }
      catch (error) {}


      event.preventDefault();

      event.stopPropagation();


      completeSuggestion();

    },
    true
  );


  /* ============================================================
     COMPLETE
     ============================================================ */

  function completeSuggestion() {

    if (
      !lotInput ||
      !lotSuggestion
    ) {

      return;

    }

    /*
     * LOT-CUSTOM 2026-08-31: capture the input + suggestion in locals BEFORE dispatching.
     *
     * Dispatching 'input' below synchronously re-enters handleInput(). The completed word is now
     * a whole word, so findBestSuggestion() returns nothing and hideSuggestion() runs -- and that
     * sets `lotInput = null` / `lotSuggestion = ''`. Every statement after the dispatch was then
     * dereferencing null: the 'change' event never fired, the cursor-to-end block failed silently
     * inside its own try/catch, and an uncaught TypeError was thrown on EVERY completion.
     */
    const input = lotInput;
    const completed = lotSuggestion;


    /*
     * Put complete word into Shopify input.
     */
    input.value =
      completed;


    /*
     * Trigger Shopify's search system.
     */
    try {

      input.dispatchEvent(
        new InputEvent(
          'input',
          {
            bubbles: true,
            composed: true,
            inputType: 'insertText',
            data: completed
          }
        )
      );

    }
    catch (error) {

      input.dispatchEvent(
        new Event(
          'input',
          {
            bubbles: true,
            composed: true
          }
        )
      );

    }


    input.dispatchEvent(
      new Event(
        'change',
        {
          bubbles: true,
          composed: true
        }
      )
    );


    /*
     * Keep cursor at the end.
     */
    try {

      input.focus();

      input.setSelectionRange(
        input.value.length,
        input.value.length
      );

    }
    catch (error) {}


    hideSuggestion();

  }


  /* ============================================================
     CLICK ARROW
     ============================================================ */

  arrowElement.addEventListener(
    'mousedown',
    event => {

      /*
       * Prevent the search input from
       * losing focus.
       */
      event.preventDefault();

    }
  );


  arrowElement.addEventListener(
    'click',
    event => {

      event.preventDefault();

      completeSuggestion();

    }
  );


  /* ============================================================
     KEEP POSITION CORRECT
     ============================================================ */

  function reposition() {

    if (
      lotInput &&
      lotSuggestion
    ) {

      positionAutocomplete(
        lotInput
      );

    }

  }


  window.addEventListener(
    'resize',
    reposition
  );


  window.addEventListener(
    'scroll',
    reposition,
    true
  );


})();
/* END LOT-CUSTOM: smart search autocomplete behaviour */
